// src/components/forms/mobile/MobileBulkUserImport.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import api from "../../../api/axios";
import { createUser } from "../../../api/users/user";
import { Upload, Download, FileSpreadsheet, Trash2, CheckCircle2, AlertCircle, Plus } from "lucide-react";

const RE = {
  username: /^[a-zA-Z0-9._-]{3,20}$/,
  password: /^(?=.*[a-z])(?=.*\d).{6,64}$/,
  email: /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/,
  phone: /^(01[016789])[-]?\d{3,4}[-]?\d{4}$/,
};

const formatPhoneKR = (raw) => {
  let digits = (raw || "").replace(/\D/g, "");
  if (digits.length === 10 && digits.startsWith("1")) {
    digits = "0" + digits;
  }
  if (digits.length > 11) digits = digits.slice(0, 11);

  if (digits.length <= 3) return digits;
  if (digits.length <= 7) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7, 11)}`;
};

const EMAIL_DOMAINS = [
  "gmail.com",
  "naver.com",
  "daum.net",
  "kakao.com",
  "outlook.com",
  "직접입력",
];

const COLS = [
  "아이디",
  "비밀번호",
  "이메일",
  "전화번호",
  "이름",
  "성",
  "회사명",
  "부서명",
  "직위명",
];

const EMPTY_ROW = {
  username: "",
  password: "",
  email: "",
  phone_number: "",
  first_name: "",
  last_name: "",
  company_id: "",
  department_id: "",
  position_id: "",
  _checked: true,
  _error: "",
};

export default function MobileBulkUserImport({ companies, departments, positions }) {
  const [rows, setRows] = useState([]);
  const [bulkEmailDomain, setBulkEmailDomain] = useState(EMAIL_DOMAINS[0]);
  const [bulkEmailDomainCustom, setBulkEmailDomainCustom] = useState("");

  const [bulkCompanyId, setBulkCompanyId] = useState("");
  const [bulkDepartmentId, setBulkDepartmentId] = useState("");
  const [bulkPositionId, setBulkPositionId] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState({ ok: 0, fail: 0, total: 0 });
  const [globalError, setGlobalError] = useState("");

  const fileRef = useRef(null);

  const mkIdx = (headerRow) => {
    const headerRaw = headerRow.map((h) => String(h || "").trim());
    const normalize = (h) =>
      String(h || "")
        .toLowerCase()
        .replace(/\(.*?\)/g, "")
        .replace(/\*/g, "")
        .replace(/\s+/g, "");
    const headerNorm = headerRaw.map(normalize);

    const ALIASES = {
      username: ["username", "아이디", "id", "사용자id"],
      password: ["password", "비밀번호", "passwd", "비번"],
      email: ["email", "이메일", "emaillocal또는전체"],
      phone_number: ["phonenumber", "phone_number", "전화번호", "휴대폰", "전화"],
      first_name: ["firstname", "first_name", "이름", "givenname", "given_name"],
      last_name: ["lastname", "last_name", "성", "성씨", "familyname", "family_name"],
      company_name: ["companyname", "company_name", "회사명", "회사", "company", "companyid", "company_id"],
      department_name: ["departmentname", "department_name", "부서명", "부서", "department", "departmentid", "department_id"],
      position_name: ["positionname", "position_name", "직위명", "직급", "직책", "position", "positionid", "position_id"],
    };

    const findIdx = (keys) => {
      for (const k of keys) {
        const i = headerNorm.indexOf(k);
        if (i !== -1) return i;
      }
      return -1;
    };

    const idx = {
      username: findIdx(ALIASES.username.map((x) => x.toLowerCase())),
      password: findIdx(ALIASES.password.map((x) => x.toLowerCase())),
      email: findIdx(ALIASES.email.map((x) => x.toLowerCase())),
      phone_number: findIdx(ALIASES.phone_number.map((x) => x.toLowerCase())),
      first_name: findIdx(ALIASES.first_name.map((x) => x.toLowerCase())),
      last_name: findIdx(ALIASES.last_name.map((x) => x.toLowerCase())),
      company_name: findIdx(ALIASES.company_name.map((x) => x.toLowerCase())),
      department_name: findIdx(ALIASES.department_name.map((x) => x.toLowerCase())),
      position_name: findIdx(ALIASES.position_name.map((x) => x.toLowerCase())),
    };

    return { idx };
  };

  const findCompanyIdByName = (name) => {
    if (!name) return "";
    const n = String(name).trim();
    const found = companies.find((c) => String(c.name).trim() === n);
    return found ? found.id : "";
  };

  const findDeptIdByName = (companyId, name) => {
    if (!name || !companyId) return "";
    const n = String(name).trim();
    const found = departments.find(
      (d) => d.company === Number(companyId) && String(d.name).trim() === n
    );
    return found ? found.id : "";
  };

  const findPosIdByName = (companyId, name) => {
    if (!name || !companyId) return "";
    const n = String(name).trim();
    const found = positions.find(
      (p) => p.company_id === Number(companyId) && String(p.name).trim() === n
    );
    return found ? found.id : "";
  };

  const validateRow = (item) => {
    const errs = [];
    if (
      !item.username ||
      !item.password ||
      !item.email ||
      !item.phone_number ||
      !item.first_name ||
      !item.last_name ||
      !item.company_id ||
      !item.position_id
    )
      errs.push("필수값 누락");
    if (item.username && !RE.username.test(item.username))
      errs.push("아이디 형식 오류");
    if (item.password && !RE.password.test(item.password))
      errs.push("비밀번호 형식 오류");
    if (item.email && !RE.email.test(item.email)) errs.push("이메일 형식 오류");
    if (item.phone_number && !RE.phone.test(item.phone_number))
      errs.push("전화번호 형식 오류");
    return errs.join(", ");
  };

  const handleFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      const wb = XLSX.read(new Uint8Array(e.target.result), { type: "array" });
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const arr = XLSX.utils.sheet_to_json(sheet, { header: 1 });
      if (!arr.length) {
        setGlobalError("시트가 비어 있습니다.");
        return;
      }
      const { idx } = mkIdx(arr[0]);

      const domainBase = (
        bulkEmailDomain === "직접입력" ? bulkEmailDomainCustom : bulkEmailDomain
      ).trim();

      const getCell = (row, key) => {
        const i = idx[key];
        return i >= 0 ? String(row[i] ?? "").trim() : "";
      };

      const list = arr
        .slice(1)
        .filter(
          (r) => r && r.length && r.some((v) => String(v || "").trim() !== "")
        )
        .map((r) => {
          const rawEmail = getCell(r, "email");
          const hasAt = rawEmail.includes("@");
          const email = hasAt
            ? rawEmail
            : domainBase && rawEmail
              ? `${rawEmail}@${domainBase}`
              : rawEmail;

          const phone = formatPhoneKR(getCell(r, "phone_number"));

          let company_id = findCompanyIdByName(getCell(r, "company_name"));
          let department_id = "";
          let position_id = "";

          if (!company_id && bulkCompanyId) {
            company_id = Number(bulkCompanyId);
          }
          if (company_id) {
            department_id = findDeptIdByName(
              company_id,
              getCell(r, "department_name")
            );
            position_id = findPosIdByName(
              company_id,
              getCell(r, "position_name")
            );
          }
          if (!department_id && bulkDepartmentId) {
            department_id = Number(bulkDepartmentId);
          }
          if (!position_id && bulkPositionId) {
            position_id = Number(bulkPositionId);
          }

          const item = {
            username: getCell(r, "username"),
            password: getCell(r, "password"),
            email,
            phone_number: phone,
            first_name: getCell(r, "first_name"),
            last_name: getCell(r, "last_name"),
            company_id: company_id === "" ? "" : Number(company_id),
            department_id: department_id === "" ? "" : Number(department_id),
            position_id: position_id === "" ? "" : Number(position_id),
          };

          return { ...item, _checked: true, _error: validateRow(item) };
        });

      const dupes = new Set();
      const seen = new Set();
      list.forEach((r) => {
        const u = (r.username || "").trim();
        if (!u) return;
        seen.has(u) ? dupes.add(u) : seen.add(u);
      });
      const final = list.map((r) =>
        dupes.has((r.username || "").trim())
          ? {
            ...r,
            _error: r._error
              ? `${r._error}, 중복 username`
              : "중복 username",
          }
          : r
      );

      setRows(final);
      setGlobalError("");
    };
    reader.readAsArrayBuffer(file);
  };

  const downloadTemplate = () => {
    const header = COLS;
    const ws = XLSX.utils.aoa_to_sheet([header]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "template");
    XLSX.writeFile(wb, "user_import_template.xlsx");
  };

  const submitSelected = async () => {
    const targets = rows.filter((r) => r._checked);
    if (!targets.length) {
      setGlobalError("선택된 행이 없습니다.");
      return;
    }

    const domainBase = (
      bulkEmailDomain === "직접입력" ? bulkEmailDomainCustom : bulkEmailDomain
    ).trim();

    const normalized = targets.map((r) => {
      const email = r.email.includes("@")
        ? r.email
        : r.email && domainBase
          ? `${r.email}@${domainBase}`
          : r.email;

      const company_id =
        r.company_id !== ""
          ? r.company_id
          : bulkCompanyId
            ? Number(bulkCompanyId)
            : "";
      const department_id =
        r.department_id !== ""
          ? r.department_id
          : bulkDepartmentId
            ? Number(bulkDepartmentId)
            : "";
      const position_id =
        r.position_id !== ""
          ? r.position_id
          : bulkPositionId
            ? Number(bulkPositionId)
            : "";

      return { ...r, email, company_id, department_id, position_id };
    });

    const withErrors = normalized.map((r) => ({
      ...r,
      _error: validateRow(r),
    }));
    const bad = withErrors.filter((r) => r._error);
    if (bad.length) {
      setRows(withErrors);
      setGlobalError(
        "형식 오류/필수값 누락 행이 있습니다. 오류를 확인하세요."
      );
      return;
    }

    setSubmitting(true);
    setProgress({ ok: 0, fail: 0, total: normalized.length });
    setGlobalError("");

    let ok = 0,
      fail = 0;
    for (const r of normalized) {
      const payload = {
        username: r.username,
        password: r.password,
        email: r.email,
        phone_number: r.phone_number,
        first_name: r.first_name,
        last_name: r.last_name,
        membership: {
          company: Number(r.company_id),
          department: r.department_id === "" ? null : Number(r.department_id),
          position: Number(r.position_id),
          is_primary: true,
        },
      };

      try {
        await createUser(payload);
        ok++;
        setRows((prev) =>
          prev.map((x) => (x === r ? { ...x, _error: "" } : x))
        );
      } catch (e) {
        fail++;
        const msg = e?.response?.data
          ? JSON.stringify(e.response.data)
          : e.message;
        setRows((prev) =>
          prev.map((x) =>
            x === r
              ? { ...x, _error: (x._error ? x._error + ", " : "") + msg }
              : x
          )
        );
      }
      setProgress({ ok, fail, total: normalized.length });
    }

    setSubmitting(false);
    if (fail === 0) {
      setRows((prev) => prev.filter((r) => !r._checked));
      alert("대량 등록 완료");
    } else {
      setGlobalError(
        "일부 행이 실패했습니다. 각 행의 오류 메시지를 확인하세요."
      );
    }
  };

  const toggleAll = (checked) =>
    setRows((prev) => prev.map((r) => ({ ...r, _checked: checked })));

  const deleteSelected = () =>
    setRows((prev) => prev.filter((r) => !r._checked));

  const bulkCanPickDepartment = !!bulkCompanyId;
  const bulkCanPickPosition = !!bulkDepartmentId;

  const bulkFilteredDepartments = useMemo(() => {
    const cid = Number(bulkCompanyId) || null;
    return departments.filter((d) => (cid ? d.company === cid : false));
  }, [departments, bulkCompanyId]);

  const bulkFilteredPositions = useMemo(() => {
    const cid = Number(bulkCompanyId) || null;
    return positions.filter((p) => (cid ? p.company_id === cid : false));
  }, [positions, bulkCompanyId]);

  const selectedCount = rows.filter((r) => r._checked).length;

  return (
    <div className="space-y-4">
      {/* 파일 업로드 섹션 */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-4 py-3 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <FileSpreadsheet className="w-4 h-4 text-blue-600" />
            <h3 className="text-sm font-semibold text-gray-800">엑셀 파일 업로드</h3>
          </div>
        </div>
        <div className="p-4 space-y-3">
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx,.xls"
            onChange={(e) => handleFile(e.target.files?.[0])}
            className="w-full text-sm file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
          />
          <div className="flex gap-2">
            <button
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium active:bg-gray-200 transition-colors"
              onClick={downloadTemplate}
            >
              <Download className="w-4 h-4" />
              템플릿 다운로드
            </button>
            <button
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium active:bg-gray-200 transition-colors"
              onClick={() => {
                if (fileRef.current) fileRef.current.value = "";
                setRows([]);
              }}
            >
              <Trash2 className="w-4 h-4" />
              초기화
            </button>
          </div>
        </div>
      </div>

      {/* 일괄 설정 섹션 */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="bg-gradient-to-r from-purple-50 to-pink-50 px-4 py-3 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <Upload className="w-4 h-4 text-purple-600" />
            <h3 className="text-sm font-semibold text-gray-800">일괄 설정</h3>
          </div>
        </div>
        <div className="p-4 space-y-4">
          {/* 이메일 도메인 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              이메일 도메인 (일괄)
            </label>
            <select
              className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all text-sm bg-white"
              value={bulkEmailDomain}
              onChange={(e) => setBulkEmailDomain(e.target.value)}
            >
              {EMAIL_DOMAINS.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
            {bulkEmailDomain === "직접입력" && (
              <input
                className="w-full mt-2 px-4 py-3 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all text-sm"
                placeholder="example.com"
                value={bulkEmailDomainCustom}
                onChange={(e) =>
                  setBulkEmailDomainCustom(e.target.value.replace(/\s/g, ""))
                }
              />
            )}
          </div>

          {/* 회사 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              회사 (일괄)
            </label>
            <select
              className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all text-sm bg-white"
              value={bulkCompanyId}
              onChange={(e) => {
                setBulkCompanyId(e.target.value);
                setBulkDepartmentId("");
                setBulkPositionId("");
              }}
            >
              <option value="">회사 선택</option>
              {companies.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>

          {/* 부서 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              부서 (일괄)
            </label>
            <select
              className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all text-sm bg-white disabled:bg-gray-50 disabled:text-gray-400"
              value={bulkDepartmentId}
              onChange={(e) => {
                setBulkDepartmentId(e.target.value);
                setBulkPositionId("");
              }}
              disabled={!bulkCanPickDepartment}
            >
              <option value="">부서 선택</option>
              {bulkFilteredDepartments.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          </div>

          {/* 직위 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              직위 (일괄)
            </label>
            <select
              className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all text-sm bg-white disabled:bg-gray-50 disabled:text-gray-400"
              value={bulkPositionId}
              onChange={(e) => setBulkPositionId(e.target.value)}
              disabled={!bulkCanPickPosition}
            >
              <option value="">직위 선택</option>
              {bulkFilteredPositions.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* 에러 메시지 */}
      {globalError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-red-800">오류</p>
            <p className="text-sm text-red-600 mt-1">{globalError}</p>
          </div>
        </div>
      )}

      {/* 데이터 목록 */}
      {rows.length > 0 && (
        <>
          <div className="flex items-center justify-between px-2">
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={selectedCount === rows.length}
                  onChange={(e) => toggleAll(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300"
                />
                <span className="font-medium text-gray-700">
                  전체 선택 ({selectedCount}/{rows.length})
                </span>
              </label>
            </div>
            <button
              onClick={deleteSelected}
              disabled={selectedCount === 0}
              className="flex items-center gap-1 px-3 py-1.5 text-sm text-red-600 bg-red-50 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed active:bg-red-100 transition-colors"
            >
              <Trash2 className="w-4 h-4" />
              삭제
            </button>
          </div>

          <div className="space-y-3">
            {rows.map((r, i) => (
              <div
                key={i}
                className={`bg-white rounded-xl border-2 overflow-hidden transition-all ${r._error
                    ? "border-red-300 bg-red-50/50"
                    : r._checked
                      ? "border-blue-300"
                      : "border-gray-200"
                  }`}
              >
                <div className="p-4">
                  <div className="flex items-start gap-3 mb-3">
                    <input
                      type="checkbox"
                      checked={!!r._checked}
                      onChange={(e) =>
                        setRows((prev) =>
                          prev.map((x, idx) =>
                            idx === i ? { ...x, _checked: e.target.checked } : x
                          )
                        )
                      }
                      className="w-5 h-5 rounded border-gray-300 mt-0.5"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-gray-900">
                          {r.username || "(아이디 없음)"}
                        </span>
                        {r._error && (
                          <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">
                            오류
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-gray-600 space-y-1">
                        <div>
                          {r.last_name} {r.first_name}
                        </div>
                        <div>{r.email}</div>
                        <div>{r.phone_number}</div>
                      </div>
                    </div>
                  </div>
                  {r._error && (
                    <div className="mt-3 pt-3 border-t border-red-200">
                      <p className="text-xs text-red-600">{r._error}</p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* 제출 버튼 */}
      {rows.length > 0 && (
        <div className="space-y-2">
          {submitting && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center justify-between text-sm">
                <span className="text-blue-800 font-medium">진행 중...</span>
                <span className="text-blue-600">
                  {progress.ok + progress.fail} / {progress.total}
                </span>
              </div>
              <div className="mt-2 h-2 bg-blue-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-blue-600 transition-all duration-300"
                  style={{
                    width: `${((progress.ok + progress.fail) / progress.total) * 100
                      }%`,
                  }}
                />
              </div>
            </div>
          )}
          <button
            className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-4 rounded-xl font-semibold text-base shadow-lg hover:shadow-xl active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            onClick={submitSelected}
            disabled={submitting || selectedCount === 0}
          >
            {submitting ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>등록 중... ({progress.ok + progress.fail}/{progress.total})</span>
              </>
            ) : (
              <>
                <CheckCircle2 className="w-5 h-5" />
                <span>선택 항목 등록 ({selectedCount}개)</span>
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
}
